const YES = 1 === 1
const NO = 1 === 0


export type RHRecordedJSONValueKind =
    "undefined" |
    "null" |
    "boolean" |
    "number" |
    "string" |
    "id" |
    "date" |
    "array" |
    "object" |
    "error" |
    "truncated" |
    "unknown"

export type RHRecordedJSONRedactionKind =
    "exact" |
    "shape" |
    "redacted" |
    "aliased"

export interface RHRecordedJSONSanitizerPolicy {
    exactPathPatterns?: string[]
    redactedPathPatterns?: string[]
    identifierPathPatterns?: string[]
    sampleArrayItemLimit?: number
    maxDepth?: number
    maxObjectKeyCount?: number
}

export interface RHRecordedJSONSanitizerContext {
    path?: string
    functionName?: string
    valueRole?: "request" | "response" | "error" | "text"
    policy?: RHRecordedJSONSanitizerPolicy
}

interface RHRecordedJSONSanitizerState {
    visitedObjects: WeakSet<object>
}

export interface RHRecordedJSONUndefinedValue {
    kind: "undefined"
}

export interface RHRecordedJSONNullValue {
    kind: "null"
}

export interface RHRecordedJSONBooleanValue {
    kind: "boolean"
    redaction: "exact"
    value: boolean
}

export interface RHRecordedJSONNumberValue {
    kind: "number"
    redaction: RHRecordedJSONRedactionKind
    sign: "negative" | "zero" | "positive" | "not-finite"
    bucket: string
    isInteger: boolean
    value?: number
}

export interface RHRecordedJSONStringValue {
    kind: "string"
    redaction: RHRecordedJSONRedactionKind
    wasEmpty: boolean
    length: number
    lengthBucket: string
    profile: string
    value?: string
}

export interface RHRecordedJSONIdentifierValue {
    kind: "id"
    redaction: "aliased"
    alias: string
    sourceKind: string
    length: number
}

export interface RHRecordedJSONDateValue {
    kind: "date"
    redaction: RHRecordedJSONRedactionKind
    isValid: boolean
    value?: string
}

export interface RHRecordedJSONArrayValue {
    kind: "array"
    redaction: "shape"
    length: number
    sampledItemCount: number
    items: RHRecordedJSONValue[]
}

export interface RHRecordedJSONObjectValue {
    kind: "object"
    redaction: "shape"
    keys: string[]
    sampledKeyCount: number
    omittedKeyCount: number
    fields: Record<string, RHRecordedJSONValue>
}

export interface RHRecordedJSONErrorValue {
    kind: "error"
    redaction: "shape"
    name: string
    message: RHRecordedJSONStringValue
}

export interface RHRecordedJSONTruncatedValue {
    kind: "truncated"
    reason: string
}

export interface RHRecordedJSONUnknownValue {
    kind: "unknown"
    type: string
}

export type RHRecordedJSONValue =
    RHRecordedJSONUndefinedValue |
    RHRecordedJSONNullValue |
    RHRecordedJSONBooleanValue |
    RHRecordedJSONNumberValue |
    RHRecordedJSONStringValue |
    RHRecordedJSONIdentifierValue |
    RHRecordedJSONDateValue |
    RHRecordedJSONArrayValue |
    RHRecordedJSONObjectValue |
    RHRecordedJSONErrorValue |
    RHRecordedJSONTruncatedValue |
    RHRecordedJSONUnknownValue


export class RHRecordedJSONSanitizer {
    
    static readonly defaultArrayItemLimit = 5
    static readonly defaultMaxDepth = 7
    static readonly defaultMaxObjectKeyCount = 40
    
    _aliasesByRawIdentifier = new Map<string, string>()
    _identifierCountsBySourceKind = new Map<string, number>()
    _redactedKeyCount = 0
    
    sanitizedValue<Value>(value: Value, context?: RHRecordedJSONSanitizerContext): RHRecordedJSONValue {
        
        const path = RHRecordedJSONSanitizer.pathFromContext(context)
        const policy = RHRecordedJSONSanitizer.policyByMergingWithDefaults(context?.policy)
        return this._sanitizedValueForPath(value, path, policy, {
            visitedObjects: new WeakSet<object>()
        })
        
    }
    
    routeByAliasingIdentifiers(route: string): string {
        
        return route.replace(/[a-fA-F0-9]{24}/g, identifier =>
            this._aliasForIdentifierWithKey(identifier, "routeId")
        )
        
    }
    
    resetSessionAliases() {
        
        this._aliasesByRawIdentifier.clear()
        this._identifierCountsBySourceKind.clear()
        this._redactedKeyCount = 0
        
    }
    
    static pathFromContext(context?: RHRecordedJSONSanitizerContext): string {
        
        if (context?.path) {
            return context.path
        }
        if (context?.valueRole) {
            return context.valueRole
        }
        return "value"
        
    }
    
    static policyByMergingWithDefaults(policy?: RHRecordedJSONSanitizerPolicy): RHRecordedJSONSanitizerPolicy {
        
        return {
            exactPathPatterns: [
                "**.status",
                "**.type",
                "**.kind",
                "**.mode",
                "**.role",
                "**.roles",
                "**.language",
                "**.languageKey",
                "**.sortDirection",
                "**.direction",
                "**.currency",
                "**.publicationVisibility",
                "**.targetType",
                "**.threadType",
                "**.eventType",
                "**.eventCategory",
                "**.entityType",
                "**.itemType",
                "**.spendCategory",
                "**.state"
            ].concat(policy?.exactPathPatterns || []),
            redactedPathPatterns: [
                "**.password",
                "**.newPassword",
                "**.oldPassword",
                "**.token",
                "**.accessToken",
                "**.invitationToken",
                "**.verificationToken",
                "**.temporaryPassword",
                "**.secret",
                "**.email",
                "**.contactEmail",
                "**.phone",
                "**.phoneNumber",
                "**.address",
                "**.postalCode",
                "**.message",
                "**.messageText",
                "**.bodyText",
                "**.answerText",
                "**.proposedAnswer",
                "**.comment",
                "**.comments",
                "**.evaluationNotes",
                "**.description",
                "**.notes",
                "**.reason",
                "**.text",
                "**.receiptHtml",
                "**.shareLink",
                "**.url"
            ].concat(policy?.redactedPathPatterns || []),
            identifierPathPatterns: [
                "**._id",
                "**.id"
            ].concat(policy?.identifierPathPatterns || []),
            sampleArrayItemLimit: policy?.sampleArrayItemLimit || RHRecordedJSONSanitizer.defaultArrayItemLimit,
            maxDepth: policy?.maxDepth || RHRecordedJSONSanitizer.defaultMaxDepth,
            maxObjectKeyCount: policy?.maxObjectKeyCount || RHRecordedJSONSanitizer.defaultMaxObjectKeyCount
        }
        
    }
    
    _sanitizedValueForPath<Value>(
        value: Value,
        path: string,
        policy: RHRecordedJSONSanitizerPolicy,
        state: RHRecordedJSONSanitizerState
    ): RHRecordedJSONValue {
        
        const depth = RHRecordedJSONSanitizer.depthForPath(path)
        const maxDepth = policy.maxDepth || RHRecordedJSONSanitizer.defaultMaxDepth
        if (depth > maxDepth) {
            return { kind: "truncated", reason: "max-depth" }
        }
        
        if (typeof value === "undefined") {
            return { kind: "undefined" }
        }
        if (value === null) {
            return { kind: "null" }
        }
        if (typeof value === "boolean") {
            return { kind: "boolean", redaction: "exact", value }
        }
        if (typeof value === "number") {
            return this._sanitizedNumberForPath(value, path, policy)
        }
        if (typeof value === "string") {
            return this._sanitizedStringForPath(value, path, policy)
        }
        if (value instanceof Date) {
            return RHRecordedJSONSanitizer._sanitizedDateForPath(value, path, policy)
        }
        if (value instanceof Error) {
            return this._sanitizedErrorForPath(value, path, policy, state)
        }
        if (Array.isArray(value)) {
            return this._sanitizedArrayForPath(value, path, policy, state)
        }
        if (typeof value === "object") {
            return this._sanitizedObjectForPath(value, path, policy, state)
        }
        
        return { kind: "unknown", type: typeof value }
        
    }
    
    _sanitizedNumberForPath(
        value: number,
        path: string,
        policy: RHRecordedJSONSanitizerPolicy
    ): RHRecordedJSONNumberValue {
        
        const sign = RHRecordedJSONSanitizer.signForNumber(value)
        const bucket = RHRecordedJSONSanitizer.bucketForNumber(value)
        const result: RHRecordedJSONNumberValue = {
            kind: "number",
            redaction: "shape",
            sign,
            bucket,
            isInteger: Number.isInteger(value)
        }
        
        if (RHRecordedJSONSanitizer.valueShouldBeRecordedExactlyAtPath(path, policy)) {
            result.redaction = "exact"
            result.value = value
        }
        
        return result
        
    }
    
    _sanitizedStringForPath(
        value: string,
        path: string,
        policy: RHRecordedJSONSanitizerPolicy
    ): RHRecordedJSONValue {
        
        const lastKey = RHRecordedJSONSanitizer.lastMeaningfulKeyForPath(path)
        if (RHRecordedJSONSanitizer.valueShouldBeRecordedAsIdentifierAtPath(value, path, policy)) {
            return {
                kind: "id",
                redaction: "aliased",
                alias: this._aliasForIdentifierWithKey(value, lastKey),
                sourceKind: RHRecordedJSONSanitizer.identifierSourceKindForKey(lastKey),
                length: value.length
            }
        }
        
        const result: RHRecordedJSONStringValue = {
            kind: "string",
            redaction: "redacted",
            wasEmpty: value.length === 0,
            length: value.length,
            lengthBucket: RHRecordedJSONSanitizer.lengthBucketForString(value),
            profile: RHRecordedJSONSanitizer.profileForString(value)
        }
        
        if (
            RHRecordedJSONSanitizer.valueShouldBeRecordedExactlyAtPath(path, policy) &&
            !RHRecordedJSONSanitizer.valueShouldBeRedactedAtPath(path, policy)
        ) {
            result.redaction = "exact"
            result.value = value
        }
        
        return result
        
    }
    
    static _sanitizedDateForPath(
        value: Date,
        path: string,
        policy: RHRecordedJSONSanitizerPolicy
    ): RHRecordedJSONDateValue {
        
        const result: RHRecordedJSONDateValue = {
            kind: "date",
            redaction: "shape",
            isValid: Number.isNaN(value.getTime()) === NO
        }
        
        if (RHRecordedJSONSanitizer.valueShouldBeRecordedExactlyAtPath(path, policy)) {
            result.redaction = "exact"
            result.value = value.toISOString()
        }
        
        return result
        
    }
    
    _sanitizedErrorForPath(
        value: Error,
        path: string,
        policy: RHRecordedJSONSanitizerPolicy,
        state: RHRecordedJSONSanitizerState
    ): RHRecordedJSONErrorValue {
        
        return {
            kind: "error",
            redaction: "shape",
            name: value.name,
            message: this._sanitizedStringForPath(
                value.message,
                RHRecordedJSONSanitizer.pathByAppendingComponent(path, "message"),
                policy
            ) as RHRecordedJSONStringValue
        }
        
    }
    
    _sanitizedArrayForPath(
        value: unknown[],
        path: string,
        policy: RHRecordedJSONSanitizerPolicy,
        state: RHRecordedJSONSanitizerState
    ): RHRecordedJSONArrayValue {
        
        const sampleArrayItemLimit = policy.sampleArrayItemLimit || RHRecordedJSONSanitizer.defaultArrayItemLimit
        const sampledItems = value.slice(0, sampleArrayItemLimit).map((item, itemIndex) =>
            this._sanitizedValueForPath(
                item,
                RHRecordedJSONSanitizer.pathByAppendingComponent(path, `[${itemIndex}]`),
                policy,
                state
            )
        )
        
        return {
            kind: "array",
            redaction: "shape",
            length: value.length,
            sampledItemCount: sampledItems.length,
            items: sampledItems
        }
        
    }
    
    _sanitizedObjectForPath(
        value: object,
        path: string,
        policy: RHRecordedJSONSanitizerPolicy,
        state: RHRecordedJSONSanitizerState
    ): RHRecordedJSONValue {
        
        if (state.visitedObjects.has(value)) {
            return { kind: "truncated", reason: "circular-reference" }
        }
        
        state.visitedObjects.add(value)
        
        const record = value as Record<string, unknown>
        const rawKeys = Object.keys(record)
        const maxObjectKeyCount = policy.maxObjectKeyCount || RHRecordedJSONSanitizer.defaultMaxObjectKeyCount
        const sampledRawKeys = rawKeys.slice(0, maxObjectKeyCount)
        const fields: Record<string, RHRecordedJSONValue> = {}
        const recordedKeys: string[] = []
        const usedRecordedKeys = new Set<string>()
        
        sampledRawKeys.forEach(rawKey => {
            const recordedKey = this._uniqueRecordedKeyForRawKey(rawKey, usedRecordedKeys)
            usedRecordedKeys.add(recordedKey)
            recordedKeys.push(recordedKey)
            fields[recordedKey] = this._sanitizedValueForPath(
                record[rawKey],
                RHRecordedJSONSanitizer.pathByAppendingComponent(path, rawKey),
                policy,
                state
            )
        })
        
        state.visitedObjects.delete(value)
        
        return {
            kind: "object",
            redaction: "shape",
            keys: recordedKeys,
            sampledKeyCount: sampledRawKeys.length,
            omittedKeyCount: rawKeys.length - sampledRawKeys.length,
            fields
        }
        
    }
    
    _uniqueRecordedKeyForRawKey(rawKey: string, usedRecordedKeys: Set<string>): string {
        
        const baseRecordedKey = this._recordedKeyForRawKey(rawKey)
        let recordedKey = baseRecordedKey
        let duplicateIndex = 2
        
        while (usedRecordedKeys.has(recordedKey)) {
            recordedKey = `${baseRecordedKey}#${duplicateIndex}`
            duplicateIndex += 1
        }
        
        return recordedKey
        
    }
    
    _recordedKeyForRawKey(rawKey: string): string {
        
        if (RHRecordedJSONSanitizer.stringLooksLikeObjectIdentifier(rawKey)) {
            return `key:${this._aliasForIdentifierWithKey(rawKey, "objectIdKey")}`
        }
        
        if (RHRecordedJSONSanitizer.objectKeyShouldBeRedacted(rawKey)) {
            this._redactedKeyCount += 1
            return `[redacted-key#${this._redactedKeyCount}]`
        }
        
        return rawKey
        
    }
    
    _aliasForIdentifierWithKey(identifier: string, key: string): string {
        
        const existingAlias = this._aliasesByRawIdentifier.get(identifier)
        if (existingAlias) {
            return existingAlias
        }
        
        const sourceKind = RHRecordedJSONSanitizer.identifierSourceKindForKey(key)
        const previousCount = this._identifierCountsBySourceKind.get(sourceKind) || 0
        const count = previousCount + 1
        const alias = `id:${sourceKind}#${count}`
        this._identifierCountsBySourceKind.set(sourceKind, count)
        this._aliasesByRawIdentifier.set(identifier, alias)
        return alias
        
    }
    
    static valueShouldBeRecordedExactlyAtPath(path: string, policy: RHRecordedJSONSanitizerPolicy): boolean {
        
        if (RHRecordedJSONSanitizer.pathMatchesAnyPattern(path, policy.exactPathPatterns)) {
            return YES
        }
        
        const lastKey = RHRecordedJSONSanitizer.lastMeaningfulKeyForPath(path)
        return RHRecordedJSONSanitizer.keyIsSafeExactKey(lastKey)
        
    }
    
    static valueShouldBeRedactedAtPath(path: string, policy: RHRecordedJSONSanitizerPolicy): boolean {
        
        if (RHRecordedJSONSanitizer.pathMatchesAnyPattern(path, policy.redactedPathPatterns)) {
            return YES
        }
        
        const lastKey = RHRecordedJSONSanitizer.lastMeaningfulKeyForPath(path)
        return RHRecordedJSONSanitizer.keyLooksSensitive(lastKey)
        
    }
    
    static valueShouldBeRecordedAsIdentifierAtPath(
        value: string,
        path: string,
        policy: RHRecordedJSONSanitizerPolicy
    ): boolean {
        
        if (RHRecordedJSONSanitizer.valueShouldBeRedactedAtPath(path, policy)) {
            return NO
        }
        if (RHRecordedJSONSanitizer.pathMatchesAnyPattern(path, policy.identifierPathPatterns)) {
            return YES
        }
        
        const lastKey = RHRecordedJSONSanitizer.lastMeaningfulKeyForPath(path)
        if (RHRecordedJSONSanitizer.keyLooksLikeIdentifierKey(lastKey)) {
            return YES
        }
        
        return RHRecordedJSONSanitizer.stringLooksLikeObjectIdentifier(value)
        
    }
    
    static pathMatchesAnyPattern(path: string, patterns?: string[]): boolean {
        
        if (!patterns) {
            return NO
        }
        
        return patterns.some(pattern => RHRecordedJSONSanitizer.pathMatchesPattern(path, pattern))
        
    }
    
    static pathMatchesPattern(path: string, pattern: string): boolean {
        
        const pathSegments = path.split(".")
        const patternSegments = pattern.split(".")
        return RHRecordedJSONSanitizer.pathSegmentsMatchPatternSegments(pathSegments, patternSegments, 0, 0)
        
    }
    
    static pathSegmentsMatchPatternSegments(
        pathSegments: string[],
        patternSegments: string[],
        pathIndex: number,
        patternIndex: number
    ): boolean {
        
        if (patternIndex === patternSegments.length) {
            return pathIndex === pathSegments.length
        }
        
        const patternSegment = patternSegments[patternIndex]
        if (patternSegment === "**") {
            for (let candidatePathIndex = pathIndex; candidatePathIndex <= pathSegments.length; candidatePathIndex += 1) {
                if (
                    RHRecordedJSONSanitizer.pathSegmentsMatchPatternSegments(
                        pathSegments,
                        patternSegments,
                        candidatePathIndex,
                        patternIndex + 1
                    )
                ) {
                    return YES
                }
            }
            return NO
        }
        
        if (pathIndex >= pathSegments.length) {
            return NO
        }
        if (patternSegment === "*") {
            return RHRecordedJSONSanitizer.pathSegmentsMatchPatternSegments(
                pathSegments,
                patternSegments,
                pathIndex + 1,
                patternIndex + 1
            )
        }
        if (patternSegment !== pathSegments[pathIndex]) {
            return NO
        }
        
        return RHRecordedJSONSanitizer.pathSegmentsMatchPatternSegments(
            pathSegments,
            patternSegments,
            pathIndex + 1,
            patternIndex + 1
        )
        
    }
    
    static pathByAppendingComponent(path: string, component: string): string {
        
        if (path.length === 0) {
            return component
        }
        return `${path}.${component}`
        
    }
    
    static depthForPath(path: string): number {
        
        if (path.length === 0) {
            return 0
        }
        return path.split(".").length
        
    }
    
    static lastMeaningfulKeyForPath(path: string): string {
        
        const pathSegments = path.split(".")
        for (let segmentIndex = pathSegments.length - 1; segmentIndex >= 0; segmentIndex -= 1) {
            const segment = pathSegments[segmentIndex]
            if (!/^\[\d+\]$/.test(segment)) {
                return segment
            }
        }
        
        return ""
        
    }
    
    static keyLooksSensitive(key: string): boolean {
        
        const lowerKey = key.toLowerCase()
        const sensitiveFragments = [
            "password",
            "token",
            "secret",
            "email",
            "phone",
            "address",
            "postal",
            "message",
            "comment",
            "description",
            "note",
            "reason",
            "text",
            "html",
            "url",
            "name"
        ]
        
        return sensitiveFragments.some(fragment => lowerKey.includes(fragment))
        
    }
    
    static keyIsSafeExactKey(key: string): boolean {
        
        const exactKeys = [
            "status",
            "type",
            "kind",
            "mode",
            "role",
            "roles",
            "language",
            "languageKey",
            "sortDirection",
            "direction",
            "currency",
            "publicationVisibility",
            "targetType",
            "threadType",
            "eventType",
            "eventCategory",
            "entityType",
            "itemType",
            "spendCategory",
            "state"
        ]
        
        return exactKeys.includes(key)
        
    }
    
    static keyLooksLikeIdentifierKey(key: string): boolean {
        
        if (key === "_id") {
            return YES
        }
        if (key === "id") {
            return YES
        }
        if (key.endsWith("Id")) {
            return YES
        }
        if (key.endsWith("ID")) {
            return YES
        }
        if (key.endsWith("Ids")) {
            return YES
        }
        if (key.endsWith("IDs")) {
            return YES
        }
        
        return NO
        
    }
    
    static identifierSourceKindForKey(key: string): string {
        
        if (key === "_id" || key === "id" || key.length === 0) {
            return "object"
        }
        
        let sourceKind = key
        sourceKind = sourceKind.replace(/IDs$/, "")
        sourceKind = sourceKind.replace(/Ids$/, "")
        sourceKind = sourceKind.replace(/ID$/, "")
        sourceKind = sourceKind.replace(/Id$/, "")
        sourceKind = sourceKind.replace(/_id$/, "")
        sourceKind = sourceKind.replace(/[^A-Za-z0-9]+/g, "-")
        
        if (sourceKind.length === 0) {
            return "object"
        }
        
        return sourceKind.charAt(0).toLowerCase() + sourceKind.slice(1)
        
    }
    
    static stringLooksLikeObjectIdentifier(value: string): boolean {
        
        return /^[a-fA-F0-9]{24}$/.test(value)
        
    }
    
    static objectKeyShouldBeRedacted(rawKey: string): boolean {
        
        if (rawKey.length > 80) {
            return YES
        }
        if (rawKey.includes("@")) {
            return YES
        }
        if (/https?:\/\//.test(rawKey)) {
            return YES
        }
        
        return NO
        
    }
    
    static signForNumber(value: number): "negative" | "zero" | "positive" | "not-finite" {
        
        if (!Number.isFinite(value)) {
            return "not-finite"
        }
        if (value < 0) {
            return "negative"
        }
        if (value > 0) {
            return "positive"
        }
        return "zero"
        
    }
    
    static bucketForNumber(value: number): string {
        
        if (!Number.isFinite(value)) {
            return "not-finite"
        }
        const absoluteValue = Math.abs(value)
        if (absoluteValue === 0) {
            return "0"
        }
        if (absoluteValue < 1) {
            return "0-1"
        }
        if (absoluteValue < 10) {
            return "1-9"
        }
        if (absoluteValue < 100) {
            return "10-99"
        }
        if (absoluteValue < 1000) {
            return "100-999"
        }
        if (absoluteValue < 10000) {
            return "1000-9999"
        }
        if (absoluteValue < 100000) {
            return "10000-99999"
        }
        if (absoluteValue < 1000000) {
            return "100000-999999"
        }
        return "1000000+"
        
    }
    
    static lengthBucketForString(value: string): string {
        
        const length = value.length
        if (length === 0) {
            return "0"
        }
        if (length <= 8) {
            return "1-8"
        }
        if (length <= 32) {
            return "9-32"
        }
        if (length <= 128) {
            return "33-128"
        }
        if (length <= 512) {
            return "129-512"
        }
        return "513+"
        
    }
    
    static profileForString(value: string): string {
        
        if (value.length === 0) {
            return "empty"
        }
        if (/^\s+$/.test(value)) {
            return "whitespace"
        }
        if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
            return "email-like"
        }
        if (/^https?:\/\//.test(value)) {
            return "url-like"
        }
        if (/^-?\d+([.,]\d+)?$/.test(value)) {
            return "number-like"
        }
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
            return "date-like"
        }
        if (value.includes("\n")) {
            return "multiline"
        }
        if (/^[A-Za-zÀ-ž\s.,;:'"!?()/-]+$/.test(value)) {
            return "words"
        }
        return "mixed"
        
    }
    
}
